import type { JSX } from 'react';
import { AvatarFrame } from '../../shared/components/AvatarFrame.js';
import { ClueSlip } from '../../shared/components/ClueSlip.js';
import type { DiscussionPlayer } from '../types.js';
import '../../shared/components/CastGrid.css';
import './CastGrid.css';

interface RevealCastProps {
  /** The full cast at resolution, including the just-struck, sorted by turnOrder. */
  players: DiscussionPlayer[];
  /** Ids shown struck (Oxblood name-plate). */
  struckIds: ReadonlySet<string>;
  /**
   * RoleReveal choreography step:
   *   0 — struck shown struck alongside the living cast.
   *   1 — struck cells fade + shrink out (exiting).
   *   2 — struck removed; the grid reflows to the living cast.
   * Omit for the guess phases, where the struck Ghost simply stays shown.
   */
  step?: 0 | 1 | 2;
}

/**
 * Static (non-interactive) cast for the reveal arc's verso — the same 3-column
 * grid geometry and the same AvatarFrame + persisted ClueSlip cells as the
 * Discussion grid, but as `<span>`s (no voting) with `data-static='yes'` so the
 * shared skeleton's hover affordance opts out. The clues stay on the page
 * throughout the reveal (the evidence remains printed); the struck cast leaves
 * via the step choreography.
 */
export function RevealCast({ players, struckIds, step }: RevealCastProps): JSX.Element {
  const shown = step === 2 ? players.filter((p) => !struckIds.has(p.id)) : players;

  return (
    <ul className="susdle-cast-grid" aria-hidden>
      {shown.map((player) => {
        const struck = struckIds.has(player.id);
        const exiting = step === 1 && struck;
        // Show the eliminated (Oxblood name-plate) styling whenever struck and
        // not yet removed (steps 0/1, and the guess phases where step is undefined).
        const eliminated = struck && step !== 2;
        return (
          <li key={player.id}>
            <span
              className={`susdle-cast-cell${exiting ? ' exiting' : ''}`}
              data-static="yes"
              data-eliminated={eliminated ? 'yes' : 'no'}
            >
              <AvatarFrame player={player} isHost={false} away={!player.connected} suppressEntry />
              <ClueSlip clue={player.clue} ownerName={player.name} suppressInk />
            </span>
          </li>
        );
      })}
    </ul>
  );
}
